import { PropsWithChildren, useLayoutEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import { cn } from '@components/primitives';

import css from './styles.module.css';

export interface PatternBannerProps extends PropsWithChildren {
  className?: string;
  trackHeightWith?: `--${string}`;
}

export function PatternBanner({
  children,
  className,
  trackHeightWith: cssVar,
}: PatternBannerProps) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!cssVar) return;

    const element = ref.current;
    if (!element) return;

    const observer = new ResizeObserver(([entry]) => {
      document.body.style.setProperty(cssVar, `${entry.contentRect.height}px`);
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      document.body.style.removeProperty(cssVar);
    };
  }, [cssVar]);

  return (
    <motion.div
      ref={ref}
      className={cn(css.root, className)}
      initial={{ height: 0 }}
      animate={{ height: 'auto' }}
      exit={{ height: 0 }}
      transition={{
        type: 'spring',
        visualDuration: 0.3,
        bounce: 0.2,
      }}
    >
      <div className={css.banner}>
        <AnimatePresence>
          <motion.div
            className={css.pattern}
            initial={{ opacity: 0, translateX: -40 }}
            animate={{ opacity: 1, translateX: 0 }}
            transition={{
              duration: 1.5,
              ease: 'easeOut',
              opacity: { duration: 2.5, ease: 'easeOut' },
            }}
          />
        </AnimatePresence>
        <span className={css.label}>{children}</span>
      </div>
    </motion.div>
  );
}
